import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addNewCategory,
  deleteCategory,
  getCategories,
  updateCategory,
} from "../../../api/faceswap/category";
import {
  BrowseImageIcon,
  DeleteIcon,
  ResetIcon,
  SaveIcon,
} from "../../../assets/icons/icons";
import { useSelectedOption } from "../../../context/media-context/SelectedOptionContext";
import { handleApiError } from "../../../utils/common";
import Button from "../../common/button";
import ErrorDisplay from "../../common/ErrorDisplay";
import WarningModal from "../../common/warning-modal/WarningModal";
import DialogBox from "../../faceswap/category/dialogbox";
import SkeletonLoader from "../../faceswap/category/event-list/SkeletonLoader";
import {
  createStyle,
  deleteStyles,
  fetchStyles,
  updateStyle,
} from "../../../api/avatar/style";
import AvatarFormDialog from "../dialog/AvatarFormDialog";
import { PlusIcon } from "@heroicons/react/24/outline";

export default function StyleTable() {
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [initialData, setInitialData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [showSaveReset, setShowSaveReset] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const queryClient = useQueryClient();
  const [changedDataArray, setChangedDataArray] = useState([]);
  const { setSelectedOption } = useSelectedOption();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isSavingData, setIsSavingData] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [validationError, setValidationError] = useState({});
  const [saveData, setSaveData] = useState({});
  const [changeDataId, setChangeDataId] = useState(new Set());
  const pagination = 10;

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["avatar-ui-getStyles"],
    queryFn: fetchStyles,
  });

  data?.data?.sort((a, b) => {
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });
  const totalPages = Math.ceil(data?.data.length / pagination);
  const currentIndex = (currentPage - 1) * pagination;
  const endIndex = currentIndex + pagination;

  // const PaginatedData = React.useMemo(() => {
  // if (data) {
  // const currentIndex = (currentPage - 1) * pagination;
  // return data.slice(currentIndex, currentIndex + pagination);
  // }
  // return [];
  // }, [data, currentPage, pagination]);

  const deleteStyleMutation = useMutation({
    mutationFn: deleteStyles,
    onSuccess: () => {
      queryClient.invalidateQueries(["avatar-ui-getStyles"]);
      toast.success("Item deleted successfully!");
    },
    onError: (error) => {
      toast.error(`Error deleting item: ${error.message}`);
    },
  });

  const updateStyleMutation = useMutation({
    mutationFn: updateStyle,
    onSuccess: () => {
      // queryClient.invalidateQueries(["avatar-ui-getStyles"]);
      setShowSaveReset(false);
      setIsEdit(false);
      toast.success("Category updated successfully!");
    },
    onError: (error) => {
      toast.error(`Error updating category: ${error.message}`);
    },
  });

  const addNewStyleMutation = useMutation({
    mutationFn: createStyle,
    onSuccess: () => {
      queryClient.invalidateQueries(["avatar-ui-getStyles"]);
      toast.success("Item added successfully!");
    },
    onError: (error) => {
      handleApiError(error);
    },
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (data) {
      setInitialData(data?.data);
      setOriginalData(data?.data);
    }
  }, [data]);

  const isEdited = (id) => {
    let result = false;
    changeDataId.forEach((value) => {
      if (id === value) {
        result = true;
      }
    });
    return result;
  };

  isEdited();

  const handleOpen = () => {
    setIsOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setIsOpenDialog(false);
    setIsEdit(false);
  };

  const handleInputChange = (id, event) => {
    const { name, type, checked, value } = event.target;

    const updatedData = initialData.map((item) => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [name]: type === "checkbox" ? checked : value,
        };

        //reset validation error
        setValidationError((prev) => ({
          ...prev,
          [name]: "",
        }));

        //saving data to object for api
        // setSaveData((prev) => ({
        //   ...prev,
        //   id: updatedItem.id,
        //   is_premium: updatedItem.is_premium,
        //   name: updatedItem.name,
        //   prompt: updatedItem.prompt,
        // }));

        //saving unique it to set
        setChangeDataId((prev) => {
          const newSet = new Set(prev);
          newSet.add(updatedItem.id);
          return newSet;
        });

        return updatedItem;
      }
      return item;
    });

    // Update the states
    setIsEdit(true);
    setInitialData(updatedData);
    setShowSaveReset(true);
  };

  const handleSaveData = (itemId) => {
    const itemToSave = initialData?.filter((item) => item.id === itemId);

    const payload = {
      id: itemToSave[0].id,
      name: itemToSave[0].name,
      prompt: itemToSave[0].prompt,
      thumbnail: itemToSave[0].imageFile,
      is_premium: itemToSave[0].is_premium,
    };

    const { id, name, prompt, thumbnail, is_premium } = payload;

    let errors = {};

    // Thumbnail validation
    if (!thumbnail) {
      // Thumbnail is optional
      delete payload.thumbnail;
    } else if (typeof thumbnail === "string") {
      errors.thumbnail = "Upload a valid file.";
    } else if (!(thumbnail instanceof File)) {
      errors.thumbnail = "Invalid file format.";
    } else {
      // Validate file type
      if (!thumbnail.type.startsWith("image/")) {
        errors.thumbnail = "Please upload an image file.";
      } else if (!["image/png", "image/jpeg"].includes(thumbnail.type)) {
        errors.thumbnail = "Only jpg and png formats are allowed.";
      }
      // Validate file size
      const MAX_FILE_SIZE = 5 * 1024 * 1024;
      if (thumbnail.size > MAX_FILE_SIZE) {
        errors.thumbnail = "File size exceeds 5MB.";
      }
    }

    // Validate is_premium
    if (typeof is_premium !== "boolean") {
      errors.is_premium = "Must be true or false.";
    }

    // If there are validation errors, update the state
    if (Object.keys(errors).length) {
      setValidationError(errors);
      return; // Do not proceed with mutation if validation fails
    }

    // If validation passes, send the payload
    updateStyleMutation.mutate(payload, {
      onSuccess: () => {
        setValidationError({});
        changeDataId.delete(itemToSave[0]?.id);
        setSaveData({});
      },
    });
  };

  const handleWarningModal = (id) => {
    setDeleteId(id);
    setIsWarningModalOpen(true);
  };

  const handleDelete = () => {
    try {
      deleteStyleMutation.mutate(deleteId);
      setIsWarningModalOpen(false);
    } catch (error) {}
    setIsWarningModalOpen(false);
  };

  const handleRetry = () => {
    refetch();
  };

  const handleSelectValue = (id) => {
    setSelectedOption(id.category_name);
  };

  if (error) {
    return <ErrorDisplay error={error} onRetry={handleRetry} />;
  }

  const handleAddNewStyle = (data, closeDialog) => {
    try {
      addNewStyleMutation.mutate(data, {
        onSuccess: () => {
          closeDialog();
        },
      });
    } catch (error) {
      console.log("Error adding new category:", error);
      throw new error(error);
    }
  };

  const handleThumbnailChange = (e, id) => {
    const file = e.target.files[0];

    // Generate a preview URL
    const imageUrl = URL.createObjectURL(file);

    // Update data and upload image
    setInitialData((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updatedItem = {
            ...item,
            thumbnail: imageUrl,
            imageFile: file,
            isUserUpload: true,
          };

          // Update changeDataId state
          setChangeDataId((prevChangeDataId) => {
            const newSet = new Set(prevChangeDataId);
            newSet.add(updatedItem.id);
            return newSet;
          });

          return updatedItem;
        }
        return item;
      }),
    );

    //resetting validation error
    setValidationError((prev) => ({
      ...prev,
      thumbnail: "",
    }));
  };
  const handleReset = (itemId) => {
    const itemToChange = originalData.find((item) => item.id === itemId); // Use `.find` to get the object
    setInitialData((prev) =>
      prev.map(
        (item) => (item.id === itemId ? { ...itemToChange } : item), // Replace the matching item, keep others as-is
      ),
    );
    changeDataId.delete(itemId);

    setValidationError({});
    setSaveData({});
  };

  return (
    <div className="relative rounded-[20px] border border-[#0075FF1A] pb-[72px]">
      <div className="my-[20px] flex flex-row items-center justify-between px-[40px]">
        <h2 className="text-left font-outfit text-[20px] font-medium leading-[25.2px] text-[#222222]">
          Styles
        </h2>
        <div className="flex flex-row gap-4">
          <Button handleOpen={handleOpen}>Create New</Button>
        </div>
      </div>
      {data?.data?.length === 0 ? (
        <p>No activity found.</p>
      ) : (
        <div className="scrollbar-hide h-[calc(100vh-350px)] overflow-hidden overflow-y-auto">
          <div className="grid grid-cols-12 gap-8 bg-[#0075FF1A] px-[40px] py-[15px]">
            <div className="col-span-1 text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#805454]">
              ID
            </div>
            <div className="text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Premium
            </div>
            <div className="col-span-3 text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Thumbnail
            </div>
            <div className="col-span-3 text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Name
            </div>

            <div className="col-span-3 text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Prompt
            </div>
          </div>
          {isLoading ? (
            Array.from({ length: 10 }).map((_, index) => (
              <div key={index + 0}>
                <SkeletonLoader />
              </div>
            ))
          ) : (
            <>
              {initialData.map((item) => (
                <div
                  key={item.id}
                  className="grid grid-cols-12 items-center gap-8 border-b border-[#0075FF1A] px-[40px] py-4"
                >
                  <div className="col-span-1 py-[0px] text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222]">
                    {item.id}
                  </div>

                  <div className="py-[0px] text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222]">
                    <input
                      type="checkbox"
                      name="is_premium"
                      checked={item.is_premium === true}
                      onChange={(event) => {
                        handleInputChange(item.id, event);
                      }}
                      className="block w-fit rounded-[10px] border border-[#0075FF1A] bg-white px-[12px] py-[15px] font-outfit text-[14px] font-normal leading-[17.64px] text-[#666666] outline-none"
                    />
                  </div>

                  <div
                    className={`col-span-3 flex flex-row items-${
                      validationError.thumbnail && saveData.id === item.id
                        ? "start"
                        : "center"
                    } gap-4`}
                  >
                    <img
                      className="h-10 w-10 rounded-lg"
                      src={`${item.isUserUpload ? item.thumbnail : `https://storage.cognise.art/${item.thumbnail}`}`}
                      alt={`img ${item.id}`}
                    />
                    <div className="w-fit">
                      <label
                        htmlFor={`thumbnail-image-style ${item.id}`}
                        className="flex w-full cursor-pointer flex-row items-center justify-start gap-2 truncate rounded-[10px] border border-[#0075FF1A] px-2 py-1 text-center font-outfit text-[14px] font-medium leading-[17.64px] text-[#0075FF]"
                      >
                        <div className="flex min-h-[34px] min-w-[34px] items-center justify-center rounded-[6px] bg-[#0075FF1A]">
                          <PlusIcon width={11.67} height={11.67} />
                        </div>
                        <div className="mr-1">Upload Thumbnail</div>
                      </label>

                      <input
                        type="file"
                        accept="image/png, image/jpeg"
                        id={`thumbnail-image-style ${item.id}`}
                        className="hidden"
                        onChange={(e) => handleThumbnailChange(e, item.id)}
                        disabled={updateStyleMutation.isPending}
                      />

                      {/* Error message */}
                      {validationError.thumbnail && saveData.id === item.id && (
                        <p className="mt-2 text-sm text-red-500">
                          {validationError.thumbnail}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="col-span-3">
                    <input
                      className="#FFFFFF block w-full rounded-[10px] border bg-[#F7FAFD] px-[12px] py-[11px] font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222] outline-none"
                      type="text"
                      name="name"
                      required
                      value={item.name}
                      onChange={(event) => handleInputChange(item.id, event)}
                    />
                    {validationError.name && saveData.id === item.id && (
                      <p className="text-sm text-red-500">
                        {validationError.name}
                      </p>
                    )}
                  </div>
                  <div className="col-span-3 py-[0px] text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222]">
                    <input
                      type="text"
                      name="prompt"
                      value={item.prompt}
                      required
                      onChange={(event) => handleInputChange(item.id, event)}
                      className="#FFFFFF block w-full rounded-[10px] border bg-[#F7FAFD] px-[12px] py-[11px] font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222] outline-none"
                    />
                    {validationError.prompt && saveData.id === item.id && (
                      <p className="text-sm text-red-500">
                        {validationError.prompt}
                      </p>
                    )}
                  </div>
                  <div className="col-span-1 flex w-fit gap-2">
                    <div className="cursor-pointer py-[16px] text-left">
                      <button
                        onClick={() => handleWarningModal(item.id)}
                        className="w-fit cursor-pointer text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#FC2D2D]"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                    {isEdited(item.id) && (
                      <div className="cursor-pointer py-[16px] text-left">
                        <button
                          onClick={() => handleSaveData(item.id)}
                          className="w-fit cursor-pointer text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#FC2D2D]"
                        >
                          <SaveIcon width="20" height="20" />
                        </button>
                      </div>
                    )}
                    {isEdited(item.id) && (
                      <div className="cursor-pointer py-[16px] text-left">
                        <button
                          onClick={() => handleReset(item.id)}
                          className="w-fit cursor-pointer text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#FC2D2D]"
                        >
                          <ResetIcon />
                        </button>
                      </div>
                    )}
                    {/* <div className="h-full cursor-pointer py-[16px] text-left">
                      <Link
                        // to={`photos?category_order=${item.id}&category_name=${item.category_name}`}
                        className="flex max-w-[20px] cursor-pointer items-center justify-center font-outfit text-[14px] font-normal leading-[17.64px] text-[#2DFCF3]"
                        onClick={() => handleSelectValue(item)}
                      >
                        <BrowseImageIcon />
                      </Link>
                    </div> */}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
      <div className="absolute bottom-5 left-0 flex w-full justify-center">
        {/* <Pagination
currentPage={currentPage}
totalPages={totalPages}
onPageChange={handlePageChange}
/> */}
      </div>
      <WarningModal
        isOpen={isWarningModalOpen}
        setIsOpen={setIsWarningModalOpen}
        onConfirm={handleDelete}
      />

      <AvatarFormDialog
        isOpen={isOpenDialog}
        onClose={handleCloseDialog}
        handleSave={handleAddNewStyle}
        isPending={addNewStyleMutation.isPending}
      />
    </div>
  );
}
